import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { ConfigurationsApiService } from '../services/configurations-api.service';
import { EscalationConfigurationActions } from './escalation-configuration.actions';
import { mapConfigurationToState } from './escalation-configuration.mapper';
import { createDefaultEscalationLevels } from './escalation-configuration.utils';

@Injectable()
export class EscalationConfigurationEffects {
  private readonly actions$ = inject(Actions);
  private readonly configurationsApi = inject(ConfigurationsApiService);

  readonly loadSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EscalationConfigurationActions.loadSettings),
      switchMap(() =>
        this.configurationsApi.getSlaEscalationSettings().pipe(
          map((response) => {
            const mapped = mapConfigurationToState(response);

            return EscalationConfigurationActions.loadSettingsSuccess({
              configurationId: mapped.configuration.id,
              escalationEnabled: mapped.escalationEnabled,
              escalationLevels: mapped.escalationLevels,
            });
          }),
          catchError((error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              return of(
                EscalationConfigurationActions.loadSettingsSuccess({
                  configurationId: null,
                  escalationEnabled: false,
                  escalationLevels: createDefaultEscalationLevels(),
                }),
              );
            }

            return of(
              EscalationConfigurationActions.loadSettingsFailure({
                error: getApiErrorMessage(error, 'Unable to load escalation settings'),
              }),
            );
          }),
        ),
      ),
    ),
  );

  readonly saveSettings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EscalationConfigurationActions.saveSettings),
      switchMap(({ payload }) =>
        this.configurationsApi.saveSlaEscalationSettings(payload).pipe(
          map((response) => {
            const mapped = mapConfigurationToState(response);

            return EscalationConfigurationActions.saveSettingsSuccess({
              message: 'Escalation settings saved successfully.',
              configurationId: mapped.configuration.id,
              escalationEnabled: mapped.escalationEnabled,
              escalationLevels: mapped.escalationLevels,
            });
          }),
          catchError((error: unknown) =>
            of(
              EscalationConfigurationActions.saveSettingsFailure({
                error: getApiErrorMessage(error, 'Failed to save escalation settings'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateEscalationStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EscalationConfigurationActions.updateEscalationStatus),
      switchMap(({ enabled, remarks }) =>
        this.configurationsApi
          .updateSlaEscalationStatus({
            is_escalation_enabled: enabled,
            remarks: remarks ?? null,
          })
          .pipe(
            map((response) =>
              EscalationConfigurationActions.updateEscalationStatusSuccess({
                message: response.message,
                escalationEnabled: response.configuration.is_escalation_enabled,
              }),
            ),
            catchError((error: unknown) =>
              of(
                EscalationConfigurationActions.updateEscalationStatusFailure({
                  error: getApiErrorMessage(
                    error,
                    enabled
                      ? 'Unable to activate Escalation Rules'
                      : 'Unable to deactivate Escalation Rules',
                  ),
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
